import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import APIClient from '../api/api';
import { Switch, notification } from 'antd';
import {
  Domain,
  DomainPickStatus,
  Job,
  JobStatus,
  JobType,
  ListDomainLeadsRequest,
  ListDomainLeadsResponse,
  removePicksResponse,
  uploadManualPicksResponse,
} from '../api/models';
import moment from 'moment-timezone';
import { formatCurrencyCents, formatPickSource, formatPickStatus } from '../utils';
import { debounce } from 'lodash';
import TagList from '../components/TagList';
import TagSelect from '../components/TagSelect';
import DomainFilterBox from '../components/DomainFilterBox';
import TagAll from '../components/TagAll';
import UploadExternalDomains from '../components/UploadExternalDomains';
import { ImportModal, ImportModalConfig } from '../components/ImportModal';
import {
  Button,
  Column,
  DataTable,
  DomainName,
  FilterPill,
  Num,
  StatusTag,
  TagTone,
} from '../components/ui';
import { PageFooter, PageHead, PageToolbar } from '../layout';
import { TextPill } from '../components/FilterPills';

export interface Props {
  pickStatus: DomainPickStatus[];
  title: string;
  portfolio?: boolean;
}

const pickStatusTone: Record<string, TagTone> = {
  pending: 'warn',
  upstreamPicked: 'success',
  userRenew: 'accent',
};

const importConfigs: Record<string, ImportModalConfig> = {
  manualPicks: {
    title: 'Add Manual Picks',
    description: 'Upload a CSV of domains to mark as manual picks.',
    action: '/v1/domainLeads/manual-picks',
    successMessage: 'Picks successfully uploaded.',
    errorMessage: 'Picks failed to upload.',
    parseSkipped: (r) => uploadManualPicksResponse.parse(r).unknownDomains,
  },
  prePicks: {
    title: 'Add Pre Picks',
    description: 'Upload a CSV of domains to pre-pick before the next run.',
    action: '/v1/domainLeads/pre-picks',
    successMessage: 'Pre picks successfully uploaded.',
    errorMessage: 'Pre picks failed to upload.',
  },
  removePicks: {
    title: 'Remove Picks',
    description: 'Upload a CSV of domains to tag as NeverPick and reset pick status.',
    action: '/v1/domainLeads/remove-picks',
    successMessage: 'Picks successfully removed.',
    errorMessage: 'Failed to remove picks.',
    callout: {
      tone: 'warn',
      title: 'Heads up',
      body: 'Domains uploaded here will be tagged with NeverPick and will never be automatically picked again by the system.',
    },
    parseSkipped: (r) => removePicksResponse.parse(r).unknownDomains,
  },
  goscores: {
    title: 'Import GoScores',
    description: 'Bulk upload GoValue scores from CSV.',
    action: '/v1/goscores',
    successMessage: 'GoScores successfully uploaded.',
    errorMessage: 'GoScores failed to upload.',
    callout: {
      tone: 'info',
      body: 'The uploaded CSV must have the columns domain_name and govalue.',
    },
  },
};

const Domains = (props: Props) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [domainLeads, setDomains] = useState<ListDomainLeadsResponse>();
  const [loading, setLoading] = useState(false);
  const [currentToken, setCurrentToken] = useState<string | undefined>();

  // Search lives in the URL so the topbar quick-search lands here.
  const filter = searchParams.get('search') || '';
  const [searchDraft, setSearchDraft] = useState(filter);
  const accountFilter = searchParams.get('account') || '';

  const [domainsFilter, setDomainsFilter] = useState<string[]>();
  const [picksToSendOnly, setPicksToSendOnly] = useState(false);
  const [filterInvalidNS, setFilterInvalidNS] = useState<boolean>(false);
  const [filterClientHold, setFilterClientHold] = useState<boolean>(false);
  const [filterTags, setFilterTags] = useState<string[]>();
  const [nsScanJob, setNsScanJob] = useState<Job>();

  const [showTagAll, setShowTagAll] = useState(false);
  const [showDomainsFilter, setShowDomainsFilter] = useState(false);
  const [showUploadDomains, setShowUploadDomains] = useState(false);
  const [updatingAutoRenew, setUpdatingAutoRenew] = useState<string | null>(null);
  const [importKey, setImportKey] = useState<keyof typeof importConfigs | null>(null);
  const [downloading, setDownloading] = useState(false);

  const isPicks = props.pickStatus.length === 1 && props.pickStatus[0] === DomainPickStatus.UPSTREAM_PICKED;

  // Sync URL `search` param with the local input draft.
  useEffect(() => {
    setSearchDraft(filter);
  }, [filter]);

  function commitSearch(value: string) {
    const next = new URLSearchParams(searchParams);
    if (value) next.set('search', value);
    else next.delete('search');
    setSearchParams(next);
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useMemo(() => debounce(commitSearch, 400), [searchParams]);

  useEffect(() => {
    if (props.portfolio) fetchLastNSScanJob();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.portfolio]);

  async function fetchLastNSScanJob() {
    try {
      const resp = await APIClient.listJobs(
        { page: 0, pageSize: 1 },
        { type: [JobType.ScanNS], status: JobStatus.JobStatusSuccess },
      );
      setNsScanJob(resp.content[0]);
    } catch (e) {
      console.error(e);
    }
  }

  useEffect(() => {
    loadDomains(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, accountFilter, picksToSendOnly, filterInvalidNS, filterClientHold, filterTags, domainsFilter]);

  function setAccountFilter(value: string) {
    const next = new URLSearchParams(searchParams);
    if (value) next.set('account', value);
    else next.delete('account');
    setSearchParams(next);
  }

  function getParams(): ListDomainLeadsRequest {
    const params: ListDomainLeadsRequest = {
      search: filter || undefined,
      account: accountFilter || undefined,
      domains: domainsFilter,
      pickStatus: props.pickStatus,
      picksToSendOnly,
      filterBadNS: filterInvalidNS || undefined,
      clientHold: filterClientHold || undefined,
      tags: filterTags,
    };
    if (props.portfolio) params.portfolio = true;
    return params;
  }

  async function loadDomains(token: string | undefined) {
    setLoading(true);
    setDomains(undefined);
    try {
      const leads = await APIClient.listDomainLeads({ page: 1, pageSize: 25, token }, getParams());
      setDomains(leads);
      setCurrentToken(token);
    } catch (e) {
      notification.error({ message: 'Failed to load domains.' });
    } finally {
      setLoading(false);
    }
  }

  async function handleAutoRenewToggle(d: Domain, newValue: boolean) {
    setUpdatingAutoRenew(d.domain);
    try {
      await APIClient.setDomainAutoRenew(d.domain, { autoRenew: newValue });
      notification.success({ message: `Auto-renew ${newValue ? 'enabled' : 'disabled'} for ${d.domain}` });
      if (domainLeads) {
        setDomains({
          ...domainLeads,
          content: domainLeads.content.map((x) => (x.domain === d.domain ? { ...x, autoRenew: newValue } : x)),
        });
      }
    } catch (e: any) {
      notification.error({ message: e?.response?.data?.error || 'Failed to update auto-renew' });
    } finally {
      setUpdatingAutoRenew(null);
    }
  }

  function onTagAllClose(updated: boolean) {
    setShowTagAll(false);
    if (updated) loadDomains(currentToken);
  }

  const portfolioColumns: Column<Domain>[] = [
    {
      key: 'domain',
      title: 'Domain',
      render: (d) => (
        <div className="flex flex-col gap-1">
          <DomainName name={d.domain} />
          {d.tags && d.tags.length > 0 && <TagList tags={d.tags} />}
        </div>
      ),
    },
    {
      key: 'accountId',
      title: 'Account',
      render: (d) => <span className="text-text-2 text-[12.5px]">{d.accountId || '—'}</span>,
    },
    {
      key: 'expires',
      title: 'Expires',
      render: (d) => (
        <span className="font-mono text-[12.5px] text-text-2 tabular-nums">
          {d.expiresAt ? moment(d.expiresAt).utc().format('YYYY-MM-DD') : '—'}
        </span>
      ),
    },
    {
      key: 'autoRenew',
      title: 'Auto Renew',
      render: (d) => (
        <Switch
          size="small"
          checked={d.autoRenew}
          loading={updatingAutoRenew === d.domain}
          disabled={updatingAutoRenew !== null}
          onChange={(checked) => handleAutoRenewToggle(d, checked)}
        />
      ),
    },
    {
      key: 'whoisGuard',
      title: 'WHOIS',
      render: (d) =>
        d.whoisGuard ? (
          <StatusTag tone="success" dot={false}>on</StatusTag>
        ) : (
          <StatusTag tone="neutral" dot={false}>off</StatusTag>
        ),
    },
    {
      key: 'nameservers',
      title: 'Nameservers',
      render: (d) => <span className="text-text-3 text-[11.5px] font-mono truncate">{d.nameservers.join(', ')}</span>,
    },
  ];

  const leadColumns: Column<Domain>[] = [
    {
      key: 'domain',
      title: 'Domain',
      render: (d) => (
        <div className="flex flex-col gap-1">
          <DomainName name={d.domain} />
          {d.tags && d.tags.length > 0 && <TagList tags={d.tags} />}
        </div>
      ),
    },
    {
      key: 'addedAt',
      title: 'First Seen',
      render: (d) => (
        <span className="font-mono text-[12.5px] text-text-3 tabular-nums">{moment(d.addedAt).format('YYYY-MM-DD')}</span>
      ),
    },
    {
      key: 'expiresAt',
      title: 'Expires',
      render: (d) => {
        if (!d.expiresAt) return <span className="text-text-3">—</span>;
        const base = moment(d.expiresAt).utc().format('YYYY-MM-DD');
        if (d.pickStatus === DomainPickStatus.PENDING) {
          const daysUntil = moment.duration(moment(d.expiresAt).add(26, 'days').diff(moment())).asDays();
          const suffix =
            daysUntil < 0
              ? `${Math.abs(daysUntil).toFixed(0)}d after`
              : `${daysUntil.toFixed(0)}d until`;
          return (
            <span className="font-mono text-[12.5px] text-text-2 tabular-nums">
              {base} <span className="text-text-3">({suffix})</span>
            </span>
          );
        }
        return <span className="font-mono text-[12.5px] text-text-2 tabular-nums">{base}</span>;
      },
    },
    {
      key: 'source',
      title: 'Source',
      render: (d) => <span className="text-text-2 text-[12.5px]">{formatPickSource(d.source)}</span>,
    },
    {
      key: 'pickStatus',
      title: 'Pick Status',
      render: (d) => (
        d.pickStatus === 'pending' && d.status?.toLowerCase().includes('active') && d.accountId
          ? <span className="text-text-3">—</span>
          : <StatusTag tone={pickStatusTone[d.pickStatus] ?? 'neutral'}>{formatPickStatus(d.pickStatus)}</StatusTag>
      ),
    },
    {
      key: 'pickedAt',
      title: 'Picked At',
      render: (d) => (
        <span className="font-mono text-[12.5px] text-text-3 tabular-nums">
          {d.pickedAt ? moment(d.pickedAt).format('YYYY-MM-DD') : '—'}
        </span>
      ),
    },
    {
      key: 'extensionsTaken',
      title: 'Ext.',
      align: 'right',
      render: (d) => <Num value={d.stats?.extensionsTaken ?? null} format="int" />,
    },
    {
      key: 'appraisedValue',
      title: 'Appraised',
      align: 'right',
      render: (d) => (
        <span className="font-mono text-[12.5px] text-text tabular-nums">
          {d.stats?.appraisedValueCents != null ? formatCurrencyCents(d.stats.appraisedValueCents) : '—'}
        </span>
      ),
    },
    {
      key: 'ahrefsDr',
      title: 'AhrefsDR',
      align: 'right',
      render: (d) => <Num value={d.stats?.ahrefsDr ?? null} format="int" />,
    },
    {
      key: 'backlinks',
      title: 'Backlinks',
      align: 'right',
      render: (d) => <Num value={d.stats?.backlinks ?? null} format="int" />,
    },
  ];

  const columns = props.portfolio ? portfolioColumns : leadColumns;

  const activeFilterCount =
    (filterTags?.length ? 1 : 0) +
    (domainsFilter?.length ? 1 : 0) +
    (picksToSendOnly ? 1 : 0) +
    (filterInvalidNS ? 1 : 0) +
    (filterClientHold ? 1 : 0) +
    (accountFilter ? 1 : 0);

  return (
    <>
      <PageHead
        title={props.title}
        subtitle={
          props.portfolio
            ? 'Your full Namecheap portfolio with renewal status and tags.'
            : 'Domains matching the selected pick status.'
        }
        actions={
          <>
            {isPicks && (
              <>
                <Button variant="ghost" onClick={() => setImportKey('manualPicks')}>
                  + Manual
                </Button>
                <Button variant="ghost" onClick={() => setImportKey('prePicks')}>
                  + Pre Picks
                </Button>
                <Button variant="ghost" onClick={() => setImportKey('removePicks')}>
                  Remove…
                </Button>
              </>
            )}
            {props.portfolio && (
              <Button variant="ghost" onClick={() => setImportKey('goscores')}>
                Import GoScores
              </Button>
            )}
            <Button variant="ghost" disabled={!domainLeads} loading={downloading} onClick={async () => {
              setDownloading(true);
              try {
                const params = APIClient.getListDomainLeadsParams(getParams());
                const formData = new FormData();
                if (domainsFilter?.length) formData.append('domains', domainsFilter.join(','));
                const resp = await APIClient.instance.post('/v1/domainLeads', formData, {
                  params: { ...params, download: true },
                  responseType: 'arraybuffer',
                });
                const ct = resp.headers['content-type'] || '';
                const isCSV = ct.includes('text/csv');
                const blob = new Blob([resp.data], { type: isCSV ? 'text/csv' : 'application/zip' });
                const a = document.createElement('a');
                a.href = URL.createObjectURL(blob);
                a.download = isCSV ? 'domains.csv' : 'domains.zip';
                a.click();
                URL.revokeObjectURL(a.href);
              } catch (e) {
                notification.error({ message: 'Download failed.' });
              } finally {
                setDownloading(false);
              }
            }}>Download</Button>
            <Button variant="primary" disabled={!domainLeads} onClick={() => setShowTagAll(true)}>
              Tag All
            </Button>
          </>
        }
      />

      <PageToolbar>
        <input
          type="text"
          value={searchDraft}
          placeholder="Search domain…"
          onChange={(e) => {
            setSearchDraft(e.target.value);
            debouncedSearch(e.target.value);
          }}
          className="bg-bg-elevated border border-transparent rounded-md px-3 py-1 w-[260px] text-[12.5px] text-text outline-none focus:border-border-strong placeholder:text-text-3"
        />
        <div className="w-[220px]">
          <TagSelect placeholder="Filter tags" disabled={!domainLeads} reload={!domainLeads} onChange={(tags) => setFilterTags(tags.length ? tags : undefined)} />
        </div>
        <FilterPill
          label="Domains"
          value={domainsFilter?.length ? `${domainsFilter.length}` : 'Any'}
          active={!!domainsFilter?.length}
          onClick={() => setShowDomainsFilter((s) => !s)}
        />
        {props.portfolio && (
          <TextPill
            label="Account"
            value={accountFilter}
            placeholder="account id…"
            onChange={setAccountFilter}
          />
        )}
        {isPicks && (
          <FilterPill
            label="Picks today"
            value={picksToSendOnly ? 'on' : 'off'}
            active={picksToSendOnly}
            onClick={() => setPicksToSendOnly((v) => !v)}
          />
        )}
        {props.portfolio && (
          <FilterPill
            label="Bad NS"
            value={filterInvalidNS ? 'on' : 'off'}
            active={filterInvalidNS}
            onClick={() => setFilterInvalidNS((v) => !v)}
          />
        )}
        {props.portfolio && (
          <FilterPill
            label="Hold"
            value={filterClientHold ? 'on' : 'off'}
            active={filterClientHold}
            onClick={() => setFilterClientHold((v) => !v)}
          />
        )}
        {props.portfolio && (
          <FilterPill
            label="Upload"
            value="…"
            onClick={() => setShowUploadDomains((s) => !s)}
          />
        )}
        {activeFilterCount > 0 && (
          <button
            type="button"
            onClick={() => {
              setDomainsFilter(undefined);
              setPicksToSendOnly(false);
              setFilterInvalidNS(false);
              setFilterClientHold(false);
              setFilterTags(undefined);
              setAccountFilter('');
            }}
            className="text-[11px] text-text-3 hover:text-text font-mono px-1"
          >
            clear ({activeFilterCount})
          </button>
        )}
        {nsScanJob && (
          <span className="ml-auto text-[10.5px] text-text-3 font-mono">
            ns-scan <b className="text-text-2">{moment(nsScanJob.finishedAt).format('MMM D HH:mm')}</b>
          </span>
        )}
      </PageToolbar>

      {(showDomainsFilter || showUploadDomains) && (
        <div className="px-6 py-3 border-b border-border bg-bg space-y-3">
          {showDomainsFilter && (
            <DomainFilterBox loading={!domainLeads} onChange={(d) => setDomainsFilter(d)} />
          )}
          {showUploadDomains && <UploadExternalDomains />}
        </div>
      )}

      {showTagAll && domainLeads && <TagAll listRequest={getParams()} onClose={onTagAllClose} />}

      {importKey && (
        <ImportModal config={importConfigs[importKey]} onClose={() => setImportKey(null)} />
      )}

      <DataTable
        columns={columns}
        data={domainLeads?.content ?? []}
        rowKey={(d) => d.domain}
        loading={loading}
        emptyMessage="No domains match these filters."
      />
      <PageFooter
        left={
          <span>
            Showing <b className="text-text-2">{domainLeads?.content.length ?? 0}</b>
          </span>
        }
        right={
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => loadDomains(domainLeads?.previous)}
              disabled={!domainLeads?.previous || loading}
              className="hover:text-text disabled:opacity-30 disabled:cursor-not-allowed"
            >
              ← prev
            </button>
            <button
              type="button"
              onClick={() => loadDomains(domainLeads?.next)}
              disabled={!domainLeads?.next || loading}
              className="hover:text-text disabled:opacity-30 disabled:cursor-not-allowed"
            >
              next →
            </button>
          </div>
        }
      />
    </>
  );
};

export default Domains;
